import { useMemo, useCallback, useState, useRef, useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragOverEvent,
  type DragEndEvent,
  type UniqueIdentifier,
} from '@dnd-kit/core';
import { getDayLabel, isToday, formatDateKey } from '@/utils/planning';
import {
  groupByDriverAndDay,
  groupOverlayByDriverAndDay,
  buildDragLayout,
  moveInLayout,
  deriveLayoutCommit,
  findLayoutContainer,
  parseTaskId,
  parseOverTarget,
  layoutCellKey,
  type EkLayout,
} from '@/utils/easykit';
import { useEasykitPlanningStore } from '@/stores/easykitPlanning';
import { useEasykitCollisionDetection } from './useEasykitCollisionDetection';
import { EasykitDayCell } from './EasykitDayCell';
import { EasykitTaskDragPreview } from './EasykitTaskDragPreview';
import type { OdooTask } from '@/types/odoo';

interface EasykitPlanningGridProps {
  days: Date[];
}

export function EasykitPlanningGrid({ days }: EasykitPlanningGridProps) {
  const { tasks, overlayTasks, showOverlayTasks, driverIds, driverMap, holidays, reorderTasks, moveTaskToCell, setDragInProgress, selectedTaskId } =
    useEasykitPlanningStore(useShallow((s) => ({
      tasks: s.tasks,
      overlayTasks: s.overlayTasks,
      showOverlayTasks: s.showOverlayTasks,
      driverIds: s.driverIds,
      driverMap: s.driverMap,
      holidays: s.holidays,
      reorderTasks: s.reorderTasks,
      moveTaskToCell: s.moveTaskToCell,
      setDragInProgress: s.setDragInProgress,
      selectedTaskId: s.selectedTaskId,
    })));

  const [activeTaskId, setActiveTaskId] = useState<number | null>(null);
  const [dragLayout, setDragLayout] = useState<EkLayout | null>(null);
  const activeTask = activeTaskId !== null ? tasks.find((t) => t.id === activeTaskId) ?? null : null;

  // Ref mirror of dragLayout — onDragEnd must read the LATEST layout, but the final
  // onDragOver's setState may not have flushed by the time onDragEnd's closure runs.
  // Reading the ref avoids committing a stale (one-step-behind) position.
  const dragLayoutRef = useRef<EkLayout | null>(null);
  // Snapshot of the layout at dragStart — the commit diffs against THIS, not against
  // the latest `grouped`, so a silentRefresh that lands mid-drag can't corrupt the diff.
  const originalLayoutRef = useRef<EkLayout | null>(null);
  const setLayout = useCallback((next: EkLayout | null) => {
    // Short-circuit when moveInLayout returned the same reference (no-op paths).
    if (next === dragLayoutRef.current) return;
    dragLayoutRef.current = next;
    setDragLayout(next);
  }, []);

  // dnd-kit multiple-containers anti-oscillation refs (from the official recipe).
  const lastOverId = useRef<UniqueIdentifier | null>(null);
  const recentlyMovedToNewContainer = useRef(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const dateKeys = useMemo(() => days.map(formatDateKey), [days]);
  const todayKey = useMemo(() => {
    const d = days.find(isToday);
    return d ? formatDateKey(d) : null;
  }, [days]);

  const grouped = useMemo(
    () => groupByDriverAndDay(tasks, days, driverIds),
    [tasks, days, driverIds],
  );

  const overlayGrouped = useMemo(
    () => showOverlayTasks
      ? groupOverlayByDriverAndDay(overlayTasks, days, driverIds)
      : new Map(),
    [overlayTasks, days, driverIds, showOverlayTasks],
  );

  const taskById = useMemo(() => {
    const m = new Map<number, OdooTask>();
    for (const t of tasks) m.set(t.id, t);
    return m;
  }, [tasks]);

  // While dragging, the live drag layout is the source of truth; otherwise grouped.
  const renderLayout = useMemo<EkLayout>(
    () => dragLayout ?? buildDragLayout(grouped, driverIds, dateKeys),
    [dragLayout, grouped, driverIds, dateKeys],
  );

  const cellTasks = useCallback(
    (driverId: number, dateKey: string): OdooTask[] =>
      (renderLayout.get(layoutCellKey(driverId, dateKey)) ?? [])
        .map((id) => taskById.get(id))
        .filter((t): t is OdooTask => t !== undefined),
    [renderLayout, taskById],
  );

  // Force the grabbing-hand cursor across the whole document while dragging.
  // body.style.cursor alone is overridden by any child with its own cursor (buttons,
  // badges, cells). Injecting a tiny !important rule covers everything reliably.
  useEffect(() => {
    if (activeTaskId === null) return;
    const styleEl = document.createElement('style');
    styleEl.textContent = `*, *::before, *::after { cursor: grabbing !important; }`;
    document.head.appendChild(styleEl);
    return () => { document.head.removeChild(styleEl); };
  }, [activeTaskId]);

  const collisionDetectionStrategy = useEasykitCollisionDetection({
    activeTaskId,
    dragLayoutRef,
    lastOverIdRef: lastOverId,
    recentlyMovedToNewContainerRef: recentlyMovedToNewContainer,
  });

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const id = parseTaskId(event.active.id);
    if (id === null) return;
    setActiveTaskId(id);
    const initial = buildDragLayout(grouped, driverIds, dateKeys);
    originalLayoutRef.current = initial;
    setLayout(initial);
    recentlyMovedToNewContainer.current = false;
    lastOverId.current = null;
    setDragInProgress(true);
  }, [grouped, driverIds, dateKeys, setLayout, setDragInProgress]);

  const handleDragOver = useCallback((event: DragOverEvent) => {
    const { active, over } = event;
    if (!over) return;
    const prev = dragLayoutRef.current;
    if (prev === null) return;
    const activeId = parseTaskId(active.id);
    const overTarget = parseOverTarget(over.id);
    if (activeId === null || !overTarget) return;

    // Above/below decision (dnd-kit multipleContainers recipe).
    // Comparing rect MIDPOINTS is biased by the user's click-offset within the source
    // card: clicking the top of a tall source can push its translated midY below the
    // target's midY even when the cursor is at the target's top, producing the
    // off-by-one "drop at top → lands at position 2" bug.
    // The recipe's fix: only treat as "after" when the active card's TOP has cleared
    // the over card's BOTTOM. Anywhere else → insert before. The user moves between
    // over items to choose the position (hovering the next item gives "above next").
    let insertAfter = false;
    if (overTarget.kind === 'task') {
      const ar = active.rect.current.translated;
      const orr = over.rect;
      if (ar && orr) insertAfter = ar.top > orr.top + orr.height;
    }

    const fromKey = findLayoutContainer(prev, activeId);
    const next = moveInLayout(prev, activeId, overTarget, { insertAfter });
    const toKey = findLayoutContainer(next, activeId);
    if (fromKey !== toKey) {
      recentlyMovedToNewContainer.current = true;
      // Clear the flag at the next animation frame so it's honored by exactly
      // one upcoming collision call (the recipe's intent).
      requestAnimationFrame(() => { recentlyMovedToNewContainer.current = false; });
    }
    setLayout(next);
  }, [setLayout]);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const layout = dragLayoutRef.current; // latest, not the possibly-stale state closure
    const original = originalLayoutRef.current; // snapshot at drag start — immune to silentRefresh mid-drag
    const activeId = parseTaskId(event.active.id);

    // Commit FIRST (store actions apply their optimistic update synchronously),
    // THEN clear the drag layout — so the grid never renders a frame from stale grouped.
    if (layout !== null && original !== null && activeId !== null) {
      const commit = deriveLayoutCommit(original, layout, activeId);
      if (commit.kind === 'reorder') {
        reorderTasks(commit.orderedIds);
      } else if (commit.kind === 'crossCell') {
        void moveTaskToCell({
          taskId: commit.taskId,
          toDate: commit.toDate,
          toDriverId: commit.toDriverId,
          insertBeforeTaskId: commit.insertBeforeTaskId,
          orderedIds: commit.orderedIds,
        });
      }
    }
    setActiveTaskId(null);
    setLayout(null);
    originalLayoutRef.current = null;
    setDragInProgress(false);
  }, [reorderTasks, moveTaskToCell, setLayout, setDragInProgress]);

  const handleDragCancel = useCallback(() => {
    setActiveTaskId(null);
    setLayout(null);
    originalLayoutRef.current = null;
    setDragInProgress(false);
  }, [setLayout, setDragInProgress]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetectionStrategy}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div className="overflow-x-auto px-4 lg:px-8 pb-8">
        <table className="w-full min-w-[900px] border-collapse border border-gray-200 bg-white text-sm table-fixed">
          <colgroup>
            <col className="w-[140px]" />
            {days.map((_, i) => (
              <col key={i} />
            ))}
          </colgroup>

          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="px-3 py-2 text-xs font-bold text-td-navy text-left border-r border-gray-200">
                Chauffeur
              </th>
              {days.map((day) => {
                const dk = formatDateKey(day);
                const today = dk === todayKey;
                const holiday = holidays[dk];
                return (
                  <th
                    key={dk}
                    className={`px-2 py-2 text-xs font-bold text-center border-r border-gray-200 last:border-r-0 ${
                      today ? 'bg-td-blue/10 text-td-blue' : 'text-td-navy'
                    }`}
                  >
                    {getDayLabel(day)}
                    {holiday && (
                      <span
                        data-testid={`easykit-holiday-${dk}`}
                        title={holiday}
                        className="mt-0.5 block truncate rounded border border-purple-200 bg-purple-50 px-1 text-[10px] font-semibold text-purple-700"
                      >
                        {holiday}
                      </span>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {driverIds.map((driverId) => {
              const driverName = driverMap.get(driverId) ?? `Chauffeur ${driverId}`;
              return (
                <tr key={driverId} className="border-b border-gray-200">
                  <td className="px-3 py-2 font-medium text-td-navy border-r border-gray-200 align-top">
                    <span className="truncate block">{driverName}</span>
                  </td>
                  {dateKeys.map((dk) => (
                    <EasykitDayCell
                      key={dk}
                      dateKey={dk}
                      driverId={driverId}
                      tasks={cellTasks(driverId, dk)}
                      overlayTasks={overlayGrouped.get(driverId)?.get(dk) ?? []}
                      isToday={dk === todayKey}
                      selectedTaskId={selectedTaskId}
                    />
                  ))}
                </tr>
              );
            })}

            {/* Afhalingen row (driverId 0) */}
            <tr className="border-b border-gray-200">
              <td className="px-3 py-2 font-medium text-td-navy border-r border-gray-200 align-top bg-amber-50">
                <span className="truncate block italic">Afhalingen</span>
              </td>
              {dateKeys.map((dk) => (
                <EasykitDayCell
                  key={dk}
                  dateKey={dk}
                  driverId={0}
                  tasks={cellTasks(0, dk)}
                  isToday={dk === todayKey}
                  selectedTaskId={selectedTaskId}
                />
              ))}
            </tr>

            {/* Empty state if no drivers */}
            {driverIds.length === 0 && (
              <tr>
                <td colSpan={days.length + 1} className="py-12 text-center text-td-muted">
                  Geen chauffeurs geconfigureerd
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Floating clone that follows the cursor while the real card sits hidden in its slot. */}
      <DragOverlay dropAnimation={null}>
        {activeTask ? <EasykitTaskDragPreview task={activeTask} /> : null}
      </DragOverlay>
    </DndContext>
  );
}
